import React from 'react';
import { cn } from '../../../lib/utils';
import {
  RECIPE_TONE_TEXT,
  type ReviewRecipe,
  type ReviewRecipeGroup,
} from '../interfaces/IReviewRecipe';

interface RecipeSectionProps {
  /** Flat list (single unnamed group). Use `groups` for sectioned lists. */
  recipes?: readonly ReviewRecipe[];
  groups?: readonly ReviewRecipeGroup[];
  activeRecipeId: string | null;
  onSelect: (id: string | null) => void;
  /** When true (default) clicking the active card clears it. Off for picker surfaces that always switch. */
  allowDeselect?: boolean;
  className?: string;
}

/**
 * Recipe cards — the shared presentation for the Blueprint "Live review
 * views" UX, lifted into core so every domain offers the same one-click
 * named-outcome cards (icon + title + description + tone, optional group
 * headings + trailing accessory) with its own domain-relevant recipes.
 */
export const RecipeSection: React.FC<RecipeSectionProps> = ({
  recipes,
  groups,
  activeRecipeId,
  onSelect,
  allowDeselect = true,
  className,
}) => {
  const resolvedGroups: readonly ReviewRecipeGroup[] = groups ?? (recipes ? [{ recipes }] : []);
  const hasAny = resolvedGroups.some((g) => g.recipes.length > 0);
  if (!hasAny) return null;

  const renderCard = (recipe: ReviewRecipe) => {
    const Icon = recipe.icon;
    const isActive = recipe.id === activeRecipeId;
    return (
      <button
        key={recipe.id}
        type="button"
        onClick={() => onSelect(isActive && allowDeselect ? null : recipe.id)}
        title={recipe.description}
        aria-pressed={isActive}
        data-testid={`recipe-${recipe.id}`}
        className={cn(
          'flex w-full items-start gap-2 rounded-md border px-2.5 py-2 text-left transition-colors',
          isActive
            ? 'border-primary/40 bg-primary/5'
            : 'border-border bg-background hover:bg-accent hover:text-accent-foreground',
        )}
      >
        <span className={cn('mt-0.5 shrink-0', RECIPE_TONE_TEXT[recipe.tone])}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[12px] font-semibold leading-tight">{recipe.title}</span>
          <span className="mt-0.5 block text-[10.5px] leading-snug text-muted-foreground">
            {recipe.description}
          </span>
        </span>
        {recipe.accessory ? <span className="shrink-0">{recipe.accessory}</span> : null}
      </button>
    );
  };

  return (
    <div className={cn('space-y-3', className)}>
      {resolvedGroups.map((group, index) => {
        if (group.recipes.length === 0) return null;
        return (
          <div key={group.heading ?? `group-${index}`} className="space-y-1">
            {group.heading && (
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.heading}
              </div>
            )}
            <div className="space-y-1">{group.recipes.map(renderCard)}</div>
          </div>
        );
      })}
    </div>
  );
};
